import React from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';

function TicketModal({ isOpen, onClose, ticket }) {
  if (!isOpen) return null;

  const getStatusClass = (status) => {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'done': return 'status-done';
      case 'in progress': return 'status-in-progress';
      case 'blocked': return 'status-blocked';
      case 'to do':
      case 'todo': return 'status-todo';
      case 'review': return 'status-review';
      case 'selected for development': return 'status-selected-for-development';
      case 'waiting for review': return 'status-waiting-for-review';
      case 'rejected': return 'status-rejected';
      case 'canceled': return 'status-canceled';
      case 'new': return 'status-new';
      default: return 'status-todo';
    }
  };

  const getPriorityClass = (priority) => {
    if (!priority) return '';
    switch (priority.toLowerCase()) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return '';
    }
  };

  const getCategoryBadgeStyle = (category) => {
    if (!category) return {};
    
    // Generate a consistent color based on category name
    const colors = [
      '#6f42c1', '#17a2b8', '#fd7e14', '#e83e8c', '#20c997', '#6610f2'
    ];
    const hash = category.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    const backgroundColor = colors[Math.abs(hash) % colors.length];
    
    return {
      backgroundColor,
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      display: 'inline-block'
    };
  };

  const getEffortBadgeStyle = (effort) => {
    if (!effort || effort <= 0) return {};
    
    // Color based on effort level (in days)
    let backgroundColor = '#28a745'; // Green for low effort
    if (effort > 5) {
      backgroundColor = '#dc3545'; // Red for high effort (>5 days)
    } else if (effort > 2) {
      backgroundColor = '#ffc107'; // Yellow for medium effort (2-5 days)
      return {
        backgroundColor,
        color: '#333', // Dark text for yellow background
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: '600',
        display: 'inline-block'
      };
    }
    
    return {
      backgroundColor,
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      display: 'inline-block'
    };
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="ticket-modal-content">
        <h2>{ticket.summary || 'No Summary'}</h2>
        
        <div className="ticket-modal-field">
          <strong>Issue Key:</strong> {ticket.issue_key}
        </div>
        
        <div className="ticket-modal-field">
          <strong>Status:</strong>{' '}
          <span className={`status-badge ${getStatusClass(ticket.status)}`}>
            {ticket.status}
          </span>
        </div>
        
        {ticket.priority && (
          <div className="ticket-modal-field">
            <strong>Priority:</strong>{' '}
            <span className={`priority-badge ${getPriorityClass(ticket.priority)}`}>
              {ticket.priority}
            </span>
          </div>
        )}

        {ticket.category && (
          <div className="ticket-modal-field">
            <strong>Category:</strong>{' '}
            <span style={getCategoryBadgeStyle(ticket.category)}>
              {ticket.category}
            </span>
          </div>
        )}

        {ticket.initial_effort_estimations && (
          <div className="ticket-modal-field">
            <strong>Initial Effort Estimation:</strong>{' '}
            <span style={getEffortBadgeStyle(ticket.initial_effort_estimations)}>
              {ticket.initial_effort_estimations} {ticket.initial_effort_estimations === 1 ? 'day' : 'days'}
            </span>
          </div>
        )}
        
        {ticket.sprint && (
          <div className="ticket-modal-field">
            <strong>Sprint:</strong> {ticket.sprint}
          </div>
        )}

        {ticket.assignee && (
          <div className="ticket-modal-field">
            <strong>Assignee:</strong> {ticket.assignee}
          </div>
        )}

        {ticket.component && (
          <div className="ticket-modal-field">
            <strong>Component:</strong> {ticket.component}
          </div>
        )}

        {ticket.labels && (
          <div className="ticket-modal-field">
            <strong>Labels:</strong> {ticket.labels}
          </div>
        )}

        {ticket.due_date && (
          <div className="ticket-modal-field">
            <strong>Due Date:</strong> {new Date(ticket.due_date).toLocaleDateString()}
          </div>
        )}

        {ticket.story_points && (
          <div className="ticket-modal-field">
            <strong>Story Points:</strong> {ticket.story_points}
          </div>
        )}
        
        <div className="ticket-modal-field">
          <strong>Description:</strong>
        </div>
        <div className="description-box">
          {ticket.description || 'No description provided'}
        </div>
      </div>
    </Modal>
  );
}

TicketModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ticket: PropTypes.object.isRequired
};

export default TicketModal;